import { FormikErrors, useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { ProjectTypeGq } from '../../../../graphql/projects/models/ProjectTypeGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { usersApiService } from '../../../../graphql/users/usersApiService';
import { useUserDefaults } from '../../../../logic/otherUtilities/useUserDefaults';
import { ProjectTabChoices } from '../../../ProjectPage/ProjectPage';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import { NewProjectFormState } from './NewProjectFormState';
import { allLanguagesOption } from '../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteLanguage';
import { globalOption } from '../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteCountry';
import { protocolOptions } from '../../../../components/muiLegacy/Forms/DomainForm';
import { KeywordTableState } from '../../../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';

export function setUpNewProjectForm(
  user: CurrentUserGq,
  startValues?: Partial<NewProjectFormState>,
  qSProjectId?: number,
  keywordTableState?: KeywordTableState
) {
  // Mutations
  const createProjectMutation = projectsApiService.mutations.projects.useCreateProject();
  const updateProjectMutation = projectsApiService.mutations.projects.useUpdateProject();
  const teamMutation = usersApiService.mutations.useEditProjectAccess();
  const updateSerpSettingsMutation = projectsApiService.mutations.projects.useUpdateProjectRankLangLoc();
  const createDomainsMutation = projectsApiService.mutations.domains.useCreateDomains();
  const addQuickSearchToProjectMutation = projectsApiService.mutations.projects.useAddQuickSearchToProject();

  const history = useHistory();
  const fallbackProjectName = useMemo(() => `${user?.givenName ?? 'User'}'s project #` + Math.round(Math.random() * 10000), []);

  const [targetQSProject, setTargetQSProject] = useState<ProjectGq>(null);
  const userDefaults = useUserDefaults(user);

  // Location
  let initialCountry = userDefaults.volumeCountry;
  let initialCity = userDefaults.volumeCity;
  let serpCountry = initialCountry;
  let serpCity = initialCity;

  if (!!qSProjectId) {
    initialCountry = startValues?.country ?? globalOption;
    initialCity = startValues?.city ?? null;
    serpCountry = startValues?.country ?? userDefaults.volumeCountry;
    serpCity = startValues?.city;
  }

  // Language
  let initialLanguage = userDefaults.volumeLanguage ?? allLanguagesOption;
  let serpLanguage = userDefaults.volumeLanguage;

  if (!!qSProjectId) {
    initialLanguage = startValues.language ?? allLanguagesOption;
    serpLanguage = startValues.language ?? userDefaults.volumeLanguage;
  }

  const initialValues: NewProjectFormState = {
    isQuickSearch: !!qSProjectId,
    projectName: '',
    clientName: user.team.name ?? '',
    domain: { protocol: protocolOptions[0].value, domain: '', type: DomainTypeGq.PROJECT },
    teamId: user.team.id,
    country: initialCountry,
    serpCountry: serpCountry,
    city: initialCity,
    serpCity: serpCity,
    currency: userDefaults.currency,
    includeZeroVolume: false,
    includeMonths: false,
    language: initialLanguage,
    serpLanguage: serpLanguage,
  };

  return {
    formBag: useFormik<NewProjectFormState>({
      initialValues: initialValues,
      enableReinitialize: true,
      validateOnChange: true,
      validate: (values) => {
        const errors: FormikErrors<NewProjectFormState> = {};
        if (!values.clientName?.length) {
          errors.clientName = 'Required';
        }
        if (!values.currency) {
          (errors as any).currency = 'Required';
        }
        return errors;
      },
      onSubmit: (values, actions) =>
        createProjectMutation({
          type: ProjectTypeGq.NORMAL_SEARCH,
        })
          .then((projectId) => {
            // Location
            let defaultLocationId = values.city?.id ?? values.country?.id;
            let defaultLocationName = values.city?.name ?? values.country?.name;
            let serpLocationId = defaultLocationId;
            let serpLocationName = defaultLocationName;

            // If global location
            if (defaultLocationId === globalOption.id) {
              defaultLocationId = null;
              defaultLocationName = null;
              serpLocationId = values.serpCity?.id ?? values.serpCountry?.id;
              serpLocationName = values.serpCity?.name ?? values.serpCountry?.name;
            }

            // Language
            let defaultLanguageId = values.language?.id;
            let defaultLanguageName = values.language?.name;
            let serpLanguageId = defaultLanguageId;
            let serpLanguageName = defaultLanguageName;

            if (defaultLanguageId == allLanguagesOption?.id) {
              defaultLanguageId = null;
              defaultLanguageName = null;
              serpLanguageId = values.serpLanguage?.id;
              serpLanguageName = values.serpLanguage?.name;
            }

            // Create promise list so we can run multiple operations in parallel
            const promises: Promise<any>[] = [];

            // Update project with data
            // This promise is sequenced to avoid race conditions when updating the project (which causes some things to not update)
            let sequencedProjectPromise = updateProjectMutation({
              id: projectId,
              type: ProjectTypeGq.NORMAL_SEARCH,
              name: values.projectName?.length ? values.projectName : fallbackProjectName,
              clientName: values.clientName,
              currency: values.currency.code,
              defaultLanguageId: defaultLanguageId,
              defaultLanguageName: defaultLanguageName,
              defaultLocationId: defaultLocationId,
              defaultLocationName: defaultLocationName,
              defaultMinSearchVolume: values.includeZeroVolume ? 0 : 1,
              defaultIncludeMonths: values.includeMonths,
            }).then((project) =>
              updateSerpSettingsMutation({
                id: projectId,
                serpLanguageId: serpLanguageId,
                serpLanguageName: serpLanguageName,
                serpLocationId: serpLocationId,
                serpLocationName: serpLocationName,
              })
            );

            if (!!values.teamId) {
              promises.push(
                teamMutation({
                  teamId: values.teamId,
                  addProjectIds: [projectId],
                  removeProjectIds: [],
                })
              );
            }

            // Add domain if relevant
            if (values.domain?.domain?.length) {
              promises.push(
                createDomainsMutation({
                  projectId: projectId,
                  domains: [values.domain],
                })
              );
            }

            if (!!qSProjectId) {
              // Adding project indirectly updates the project - when stats are update
              sequencedProjectPromise = sequencedProjectPromise
                .then((project) =>
                  addQuickSearchToProjectMutation({
                    keywordQuery: {
                      filtering: keywordTableState.backendFilterState,
                      selecting: keywordTableState.backendSelectionState,
                    },
                    targetProjectId: projectId,
                  }).then(() => project)
                    .finally(() => setTargetQSProject(project))
                );
            }

            promises.push(sequencedProjectPromise);
            // Wait for all mutations
            return Promise.all(promises).then(() => projectId);
          })
          .then((projectId) => {
            if (!qSProjectId) {
              history.push(`/${projectId}/${ProjectTabChoices.Manage}`);
            }
          }),

    }),
    fallbackProjectName,
    targetQSProject,
  };
}
